import React from 'react';
import PropTypes from 'prop-types';
import { Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { FormQuestionTypes } from '../../models/survey/FormQuestionTypes';
import { FormItemTypes } from '../../models/survey/FormItemTypes';
import SubmissionFreeText from './SubmissionFreeText';
import SubmissionSingleChoice from './SubmissionSingleChoice';
import SubmissionSingleChoiceTable from './SubmissionSingleChoiceTable';
import SubmissionMultiChoice from './SubmissionMultiChoice';
import SubmissionIconRating from './SubmissionIconRating';
import AreaHeader from './AreaHeader';
import SubmissionRanking from './SubmissionRanking';
import RespondentProfileSubmissionForm from './RespondentProfileSubmissionForm';
import SubmissionLocationChoice from './SubmissionLocationChoice';
import SubmissionLocationsProposal from './SubmissionLocationsProposal';
import SubmissionFilesUpload from './SubmissionFilesUpload';
import RespondentVerificationForm from './RespondentVerificationForm';
import VoterIdentificationMethods from './responderVerification/VoterIdentificationMethods';

const buildFileUploadAcceptedMimeTypesList = (question) => {
  if (!question.fileTypesFiltering) {
    return null;
  }
  let types = [];
  if (question.allowDocuments) types = [...types,
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
    'application/vnd.ms-word.document.macroEnabled.12',
    'application/vnd.ms-word.template.macroEnabled.12',
    'application/vnd.oasis.opendocument.text',
    'application/xml',
    'application/json',
    'text/plain',
  ];
  if (question.allowPdfs) types = [...types, 'application/pdf'];
  if (question.allowSpreadsheets) types = [...types,
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.oasis.opendocument.spreadsheet',
  ];
  if (question.allowVideos) types = [...types, 'video/*'];
  if (question.allowPresentations) types = [...types,
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.oasis.opendocument.presentation',
  ];
  if (question.allowImages) types = [...types, 'image/*'];
  if (question.allowAudio) types = [...types, 'audio/*'];
  return types.join(', ');
};

const renderQuestion = (formItem, number, name, readonly, values, proposedLocations, initialLocation, surveyId, onDeleteUploadedFile, tenant) => {
  switch (formItem.questionType) {
    case FormQuestionTypes.singleChoice:
      return <SubmissionSingleChoice key={formItem.id} name={name} number={number} questionText={formItem.questionText} answers={formItem.answers} readonly={readonly} isOptional={formItem.isOptional} allowOwnAnswer={formItem.allowOwnAnswer} ownAnswerName={formItem.ownAnswerName} displayImages={formItem.displayImages} surveyId={surveyId} tenant={tenant} />;
    case FormQuestionTypes.singleChoiceTable:
      return <SubmissionSingleChoiceTable key={formItem.id} name={name} number={number} questionText={formItem.questionText} questions={formItem.questions} answers={formItem.answers} readonly={readonly} isOptional={formItem.isOptional} allowOwnAnswer={formItem.allowOwnAnswer} ownAnswerName={formItem.ownAnswerName} values={values} />;
    case FormQuestionTypes.multiChoice:
      return <SubmissionMultiChoice key={formItem.id} name={name} number={number} questionText={formItem.questionText} answers={formItem.answers} readonly={readonly} isOptional={formItem.isOptional} allowOwnAnswer={formItem.allowOwnAnswer} ownAnswerName={formItem.ownAnswerName} displayImages={formItem.displayImages} surveyId={surveyId} tenant={tenant} maxAnswers={formItem.maxAnswers} minAnswers={formItem.minAnswers} />;
    case FormQuestionTypes.iconRating:
      return <SubmissionIconRating key={formItem.id} name={name} number={number} ratingIcon={formItem.ratingIcon} questionText={formItem.questionText} answers={formItem.answers} readonly={readonly} isOptional={formItem.isOptional} />;
    case FormQuestionTypes.ranking:
      return <SubmissionRanking key={formItem.id} name={name} number={number} questionText={formItem.questionText} answers={formItem.answers} readonly={readonly} isOptional={formItem.isOptional} />;
    case FormQuestionTypes.freeText:
      return <SubmissionFreeText key={formItem.id} name={name} number={number} questionText={formItem.questionText} placeholder={formItem.answerPlaceholderText} readonly={readonly} isOptional={formItem.isOptional} />;
    case FormQuestionTypes.locationChoice:
      return <SubmissionLocationChoice key={formItem.id} name={name} number={number} questionText={formItem.questionText} questions={formItem.questions} answers={formItem.answers} readonly={readonly} isOptional={formItem.isOptional} />;
    case FormQuestionTypes.locationsProposal:
      return (<SubmissionLocationsProposal
        key={formItem.id}
        name={name}
        number={number}
        questionText={formItem.questionText}
        choiceLimits={formItem.choiceLimits}
        allowExistingProposalChoice={formItem.allowExistingProposalChoice}
        allowedAreas={formItem.allowedAreas}
        readonly={readonly}
        isOptional={formItem.isOptional}
        proposedLocations={proposedLocations ? proposedLocations[formItem.id] || [] : []}
        initialLocation={initialLocation} />);
    case FormQuestionTypes.filesUpload: {
      return (<SubmissionFilesUpload
        key={formItem.id}
        name={name}
        number={number}
        surveyId={surveyId}
        questionId={formItem.id}
        questionText={formItem.questionText}
        fileSizeLimitMegabytes={formItem.fileSizeLimitMegabytes}
        filesCountLimit={formItem.filesCountLimit}
        acceptedMimeTypes={buildFileUploadAcceptedMimeTypesList(formItem)}
        readonly={readonly}
        isOptional={formItem.isOptional}
        onDeleteUploadedFile={fileId => onDeleteUploadedFile(formItem.id, fileId)}
      />);
    }
    default:
      return null;
  }
};

const renderFormItems = (formItems, readonly, values, proposedLocations, initialLocation, surveyId, onDeleteUploadedFile, tenant) => {
  let questionIndex = 0;
  let outerNumber = 0;
  return formItems.map(formItem => {
    outerNumber++;
    let innerNumber = 0;
    switch (formItem.type) {
      case FormItemTypes.area:
        return (<React.Fragment key={formItem.id}>
          <AreaHeader name={formItem.areaName} />
          {formItem.questions.map(question => {
            innerNumber++;
            const questionItem = renderQuestion(question, `${outerNumber}.${innerNumber}`, `answers[${questionIndex}]`, readonly, values, proposedLocations, initialLocation, surveyId, onDeleteUploadedFile, tenant);
            questionIndex++;
            return questionItem;
          })}
        </React.Fragment>);
      case FormItemTypes.question: {
        const questionItem = renderQuestion(formItem, outerNumber, `answers[${questionIndex}]`, readonly, values, proposedLocations, initialLocation, surveyId, onDeleteUploadedFile, tenant);
        questionIndex++;
        return questionItem;
      }
      default:
        return null;
    }
  });
};

const getDefaultSelectedAnswers = question => question.questionType === FormQuestionTypes.ranking ? question.answers.map(a => a.id) : [];
const getDefaultOwnAnswers = question => question.questionType === FormQuestionTypes.singleChoiceTable ? question.answers.map(() => null) : [];

const getIdentificationMethod = identificationMethods => identificationMethods && identificationMethods.length === 1
  ? identificationMethods[0].id
  : null;

const initializeFormValues = (form, respondentProfile, identificationMethods) => (
  {
    answers: form
      .map(formItem => {
        if (formItem.type === FormItemTypes.question) {
          return [{ questionId: formItem.id, selectedAnswersIds: getDefaultSelectedAnswers(formItem), ownAnswers: getDefaultOwnAnswers(formItem), files: [] }];
        } else if (formItem.type === FormItemTypes.area) {
          return formItem.questions.map(question => ({ questionId: question.id, selectedAnswersIds: getDefaultSelectedAnswers(question), ownAnswers: getDefaultOwnAnswers(question), files: [] }));
        }
      })
      .reduce((previous, current) => (previous.concat(current))),
    respondentProfileValues: respondentProfile
      .map(item => ({ attributeId: item.id })),
    respondent: {},
    voterIdentificationMethodId: getIdentificationMethod(identificationMethods),
  });

const renderRespondentProfile = (respondentProfile, approvals, readonly, tenantName) => (
  <RespondentProfileSubmissionForm respondentProfile={respondentProfile} readonly={readonly} approvals={approvals} tenantName={tenantName} />
);

const renderRespondentVerification = (email, readonly) => (
  <RespondentVerificationForm email={email} readonly={readonly} />
);

const SurveySubmissionForm = ({ surveyId, form, respondentProfile, approvals, defaultValues, validate, onSubmit, readonly, tenantName, proposedLocations, initialLocation, onDeleteUploadedFile, unit, participantsVerification, identificationMethods, actions }) => (
  <Form
    onSubmit={onSubmit ? onSubmit : () => { }}
    validate={validate}
    keepDirtyOnReinitialize
    initialValues={defaultValues || initializeFormValues(form, respondentProfile, identificationMethods)}
    mutators={{
      change: (args, state, utils) => {
        utils.changeValue(state, args[0], () => args[1]);
      },
      ...arrayMutators
    }}
    render={({ handleSubmit, values, submitting, form: finalForm }) => (
      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="box-header-outside">
            <div className="col-sm-12">
              <div className="header">
                <h2>Formularz konsultacji</h2>
              </div>
            </div>
          </div>
          {renderFormItems(form, readonly, values, proposedLocations, initialLocation, surveyId, onDeleteUploadedFile, unit.url)}
        </div>
        {respondentProfile && respondentProfile.length > 0
          ? renderRespondentProfile(respondentProfile, approvals, readonly, tenantName)
          : null}
        {identificationMethods && identificationMethods.length 
          ? <VoterIdentificationMethods
              voterIdentificationMethods={identificationMethods}
              change={finalForm.mutators.change}
              voterIdentificationMethodId={values.voterIdentificationMethodId}
              actions={actions}
              respondent={values.respondent}
            /> : null}
        {participantsVerification && participantsVerification.email
          ? renderRespondentVerification(participantsVerification.email, readonly)
          : null}
        <div className="row">
          <div className="col-xs-12" style={{ marginRight: 15 }}>
            <button type="submit" disabled={submitting || readonly} className="btn btn-send">
              {submitting ? 'Zapisywanie...' : 'Wyślij formularz'} <i className="fa fa-paper-plane" />
            </button>
          </div>
        </div>
      </form>
    )}
  />
);

SurveySubmissionForm.propTypes = {
  form: PropTypes.array.isRequired,
  approvals: PropTypes.object,
  tenantName: PropTypes.string,
  respondentProfile: PropTypes.array,
  defaultValues: PropTypes.object,
  validate: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  children: PropTypes.node,
  readonly: PropTypes.bool,
  proposedLocations: PropTypes.object,
  initialLocation: PropTypes.object,
  surveyId: PropTypes.string,
  onDeleteUploadedFile: PropTypes.func.isRequired,
  unit: PropTypes.object,
  participantsVerification: PropTypes.object.isRequired,
  identificationMethods: PropTypes.array,
  actions: PropTypes.object,
};

export default SurveySubmissionForm;



// WEBPACK FOOTER //
// ./src/components/consultationSurveys/SurveySubmissionForm.js